import CustomReusableModal from "@bevco/Components/CustomReusableModal";
import { styles as orderDocumentDownloadStyles } from "@bevco/Components/OrderDocumentDownload";
import { getPdf } from "@bevco/Services/OrderService";
import setAddedToCartModalOpen from "@bevco/Store/Handlers/Custom/CustomSetAddedToCartModal";
import setSpoolModalOpen from "@bevco/Store/Handlers/Custom/CustomSetSpoolModal";
import changeOrder from "@bevco/Store/Handlers/CustomModals/ChangeOrder";
import returnRequest from "@bevco/Store/Handlers/CustomModals/ReturnRequest";
import showHideCustomReusableModal from "@bevco/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import { setGA4Event } from "@bevco/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { canCancelOrder } from "@insite/client-framework/Store/Pages/OrderDetails/OrderDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import { parseOrderType } from "@bevco/Utilities/Utilities";
/* Valtech Custom Code - End */

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const enum buttons {
    cancel = "Cancel",
    print = "Print",
    email = "Email",
    reorder = "Reorder",
    rma = "Rma",

    /* Verndale Custom Feature - New Buttons */
    changeOrder = "ChangeOrder",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    const session = getSession(state);
    // Verndale Custom Code - ticket: IEIXOP-4730
    const cartLines = getCurrentCartState(state).value?.cartLines;

    return {
        order,
        canCancel: canCancelOrder(state, order),
        /* Verndale Custom Feature - Replace RMA condition */
        allowRma: order?.status === "Completed", // allowRma: canRmaOrder(state, order),
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        showAddedToCartModal: (settingsCollection as any).customCartSettings?.showCustomAddToCartModal === true,
        isReordering: state.pages.orderDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,

        /* Verndale Custom Feature - New button conditions */
        allowChangeOrder: order?.status === "Open" && session.isAuthenticated,
        orderDetailsUrl: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
        pageTitle: getCurrentPage(state).fields.title,
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        billTo: getBillToState(state, state.context?.session?.billToId).value,
        cartLines,
        hideActionButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideActionButtons,
        hideDownloadDocumentButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideDownloadDocumentButtons,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,

    /* Verndale Custom Feature - New Methods */
    showHideCustomReusableModal,
    returnRequest,
    changeOrder,
    setSpoolModalOpen,
    setAddedToCartModalOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;

    /* Verndale Custom Feature - New styles */
    changeOrderButton?: ButtonPresentationProps;
    changeOrderClickable?: ClickablePresentationProps;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "tertiary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

const OrderDetailsButtonSet: React.FC<Props> = ({
    order,
    cancelOrder,
    reorder,
    canCancel,
    allowRma,
    fields,
    rmaLink,
    canReorderItems,
    showAddToCartConfirmationDialog,
    showAddedToCartModal,
    isReordering,
    isUpdatingCart,
    history,
    isVmiOrderDetailsPage,

    /* Verndale Custom Feature - New Variables & Methods */
    showHideCustomReusableModal,
    returnRequest,
    allowChangeOrder,
    changeOrder,
    orderDetailsUrl,
    pageTitle,
    isAuthenticated,
    billTo,
    cartLines,
    setSpoolModalOpen,
    setAddedToCartModalOpen,
    hideActionButtons,
    hideDownloadDocumentButtons,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [downloading, setDownloading] = React.useState(false);
    const [downloadErrored, setDownloadErrored] = React.useState(false);

    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!order) {
        return null;
    }

    /* Valtech Custom Code - Start */
    const {status, orderTotal: value} = order;
    const orderType = parseOrderType(order.properties?.orderChannel);
    /* Valtech Custom Code - End */
    const btnDownloadLabel = translate("Download");

    const orderNumber =
        order.webOrderNumber && order.webOrderNumber !== "" ? order.webOrderNumber : order.erpOrderNumber;

    const onClickCancel = () => {
        cancelOrder({ orderNumber });
    };

    const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    };

    const onClickReorder = () => {
        // Verndale Custom Code - ticket: IEIXOP-4730
        if (cartLines?.length && order?.orderLines?.length) {
            const matchingLines = order.orderLines.filter(orderLine =>
                cartLines.some(
                    cl =>
                        cl.productId === orderLine.productId &&
                        cl.properties?.spoolLength !== undefined &&
                        cl.properties?.spoolSelected !== undefined
                )
            );
            const matchingCount = matchingLines.length; // Number of matching items
            const totalCount = order.orderLines.length; // Total number of items in the order
            if (matchingCount > 0 && matchingCount === totalCount) {
                if (showAddedToCartModal) {
                    setAddedToCartModalOpen({
                        isOpen: true,
                        spoolNoticeId: "order-already-in-cart",
                    });
                } else {
                    setSpoolModalOpen({ isOpen: true, id: "order-already-in-cart" });
                }
                return;
            }

        }

        reorder({
            onSuccess: () => {
                // Verndale Code
                if (isAuthenticated) {
                    const sapAccountNumber =
                        billTo && billTo.customerNumber ? billTo.customerNumber.replace(/^0+/, "") : "__IS_LOADING__";
                    setGA4Event([], "reorder", { sapAccountNumber });
                }

                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toasterContext.addToast({
                    children: (
                        <>
                            {isVmiOrderDetailsPage ? (
                                <OrderDetailPageTypeLink
                                    title={order.webOrderNumber}
                                    orderNumber={order.webOrderNumber || order.erpOrderNumber}
                                    isVmiOrderDetailsPage={isVmiOrderDetailsPage}
                                />
                            ) : (
                                <span>
                                    <Link
                                        href={`${orderDetailsUrl}?orderNumber=${
                                            order.webOrderNumber || order.erpOrderNumber
                                        }`}
                                        data-test-selector="orderNumber"
                                    >
                                        {order.webOrderNumber}
                                    </Link>{" "}
                                    {translate("Added to Cart")}
                                </span>
                            )}
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };

    const onClickRma = (url: string) => {
        /* Verndale Custom Feature - Replace navigation with custom modal */
        showHideCustomReusableModal({
            orderNumber,
            key: "return-request",
            show: true,
        });
        // history.push(`${url}?orderNumber=${order.webOrderNumber || order.erpOrderNumber}`);
    };

    /* Verndale Custom Feature - Show Change Order Modal */
    const onClickChangeOrder = () => {
        showHideCustomReusableModal({
            orderNumber,
            key: "change-order",
            show: true,
        });
    };

    const download = async () => {
        try {
            setDownloading(true);
            const { results } = (await getPdf({
                documentId: (order as any).guid,
                type: "order acknowledgement",
            })) as any;
            const base64String = results.pdF_B64STR;
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            setDownloading(false);

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `order_${order.erpOrderNumber || order.webOrderNumber}.pdf`;

            const currentLocation = window.location;

            setGA4Event([], "sap_file_download", {
                file_name: link.download,
                file_extension: link.download.split(".").pop(),
                link_text: btnDownloadLabel,
                link_url: currentLocation.href,
                pageTitle,
            });

            /* Valtech Custom Code - Start */
            setGA4Event([], "download_order", { status, orderType, value });
            /* Valtech Custom Code - End */

            link.click();
        } catch {
            setDownloading(false);
            setDownloadErrored(true);
        }
    };

    const buttonList: ButtonMapper = {};
    if (order.canAddToCart && canCancel) {
        buttonList[buttons.cancel] = {
            button: (
                <Button {...styles.cancelButton} onClick={onClickCancel} data-test-selector="orderDetails_cancelButton">
                    {translate("Cancel")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.cancelClickable} onClick={onClickCancel}>
                    {translate("Cancel")}
                </Clickable>
            ),
        };
    }
    
    if (order.canAddToCart && canReorderItems && order.status !== "Open" && !hideActionButtons) {
        buttonList[buttons.reorder] = {
            button: (
                <>
                    {!isReordering && (
                        <Button
                            {...styles.reorderButton}
                            onClick={onClickReorder}
                            disabled={isUpdatingCart}
                            data-test-selector="orderDetails_reorderButton"
                        >
                            {translate("Reorder")}
                        </Button>
                    )}
                    {isReordering && <LoadingSpinner {...styles.spinner} />}
                </>
            ),
            clickable: (
                <>
                    {!isReordering && (
                        <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isUpdatingCart}>
                            {translate("Reorder")}
                        </Clickable>
                    )}
                    {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                </>
            ),
            showButtonOnTablet: true,
        };
    }

    if (allowRma && rmaLink && !hideActionButtons) {
        buttonList[buttons.rma] = {
            button: (
                <Button
                    {...styles.rmaButton}
                    onClick={() => onClickRma(rmaLink.url)}
                    data-test-selector="orderDetails_returnRequestButton"
                >
                    {/* Verndale Custom Feature - Change translation */}
                    {translate("Return Request (RMA)")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.rmaClickable} onClick={() => onClickRma(rmaLink.url)}>
                    {/* Verndale Custom Feature - Change translation */}
                    {translate("Return Request (RMA)")}
                </Clickable>
            ),
        };
    }

    if (allowChangeOrder && !hideActionButtons) {
        buttonList[buttons.changeOrder] = {
            button: (
                <Button
                    {...styles.changeOrderButton}
                    onClick={onClickChangeOrder}
                    data-test-selector="orderDetails_changeOrderButton"
                >
                    {translate("Change Order")}
                </Button>
            ),
            clickable: (
                <Clickable {...styles.changeOrderClickable} onClick={onClickChangeOrder}>
                    {translate("Change Order")}
                </Clickable>
            ),
        };
    }

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            {downloading && (
                <StyledWrapper {...orderDocumentDownloadStyles.overlay}>
                    <StyledWrapper {...orderDocumentDownloadStyles.toast}>
                        <StyledWrapper {...orderDocumentDownloadStyles.loader} />
                        <Typography size={14}>{translate("Preparing your document for download...")}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {downloadErrored && (
                <StyledWrapper
                    {...orderDocumentDownloadStyles.overlay}
                    className="error"
                    onClick={() => setDownloadErrored(false)}
                >
                    <StyledWrapper {...orderDocumentDownloadStyles.toast}>
                        <Typography {...orderDocumentDownloadStyles.error}>
                            {translate(
                                "We're sorry, this document is not currently available. Please try again later.",
                            )}
                        </Typography>
                        <StyledWrapper as="button" {...orderDocumentDownloadStyles.close}>
                            x
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <Hidden {...styles.buttonHidden} data-test-selector="orderDetails_standardButtons">
                {buttonsToRender.map((button, index) => (
                    <StyledWrapper {...styles.buttonWrapper} key={index}>
                        {button.button}
                    </StyledWrapper>
                ))}
                {!hideDownloadDocumentButtons && (
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button onClick={download}>{btnDownloadLabel}</Button>
                    </StyledWrapper>
                )}
            </Hidden>
            <Hidden {...styles.tabletButtonHidden}>
                {buttonsToRender
                    .filter(o => o.showButtonOnTablet)
                    .map((button, index) => (
                        <StyledWrapper {...styles.buttonWrapper} key={index}>
                            {button.button}
                        </StyledWrapper>
                    ))}
                {!hideDownloadDocumentButtons && (
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button onClick={download}>{translate("Download")}</Button>
                    </StyledWrapper>
                )}
            </Hidden>
            <Hidden {...styles.tabletMenuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender
                        .filter(o => !o.showButtonOnTablet)
                        .map((button, index) => (
                            <div key={index}>{button.clickable}</div>
                        ))}
                    {!hideDownloadDocumentButtons && (
                        <Clickable {...styles.changeOrderClickable} onClick={download}>
                            {translate("Download")}
                        </Clickable>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.menuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender.map((button, index) => (
                        <div key={index}>{button.clickable}</div>
                    ))}
                </OverflowMenu>
            </Hidden>

            <CustomReusableModal
                id="change-order"
                headline={translate("Change Order")}
                subHeadline={translate("Why would you like to change this order?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter changes needed to your order")}
                submitLabel={translate("Request Change")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Change_Message_Error") as string}
                submitRequest={changeOrder}
                // Valtech Custom Code
                trackingDetails={{trackingKey: "change_order", status, orderType, value}}
            ></CustomReusableModal>

            <CustomReusableModal
                id="return-request"
                headline={translate("Return Request")}
                subHeadline={translate("Why would you like to return part or all of this order?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter items to return")}
                submitLabel={translate("Submit Request")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("ReturnRequest_Message_Error") as string}
                submitRequest={returnRequest}
            ></CustomReusableModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsButtonSet) as any) as any,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        displayName: "Order Details Button Set",
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Order",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.changeOrder } },
                    { fields: { name: buttons.rma } },
                    { fields: { name: buttons.print } },
                    { fields: { name: buttons.email } },
                    { fields: { name: buttons.cancel } },
                    { fields: { name: buttons.reorder } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the order generates an email attachment using the Order Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
